import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MenuIcon, CloseIcon } from './Icons';

const Navbar = ({ currentPage, navigateToPage, personalInfo }) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const navItems = [
        { id: 'about', label: 'About' },
        { id: 'work', label: 'Work' },
        { id: 'experience', label: 'Experience' },
        // { id: 'education', label: 'Education' },
        { id: 'blog', label: 'Blog' },
        { id: 'shelf', label: 'Shelf' },
    ];

    const handleNavItem = (item) => {
        if (item.route) {
            navigate(item.route);
            setIsDrawerOpen(false);
        } else {
            scrollToSection(item.id);
        }
    };

    const isNavItemActive = (item) => {
        if (item.route) return location.pathname === item.route;
        return currentPage === item.id && location.pathname === '/';
    };

    // Track scroll for visibility and shadow effect
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 100);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToSection = (sectionId) => {
        if (location.pathname !== '/') {
            navigate('/');
            setTimeout(() => {
                const element = document.getElementById(sectionId);
                if (element) {
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            }, 100);
        } else {
            const element = document.getElementById(sectionId);
            if (element) {
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        }
        navigateToPage(sectionId);
        setIsDrawerOpen(false);
    };

    const scrollToTop = () => {
        if (location.pathname !== '/') {
            navigate('/');
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
        navigateToPage('intro');
    };

    const toggleDrawer = () => {
        setIsDrawerOpen(!isDrawerOpen);
    };

    const closeDrawer = () => {
        setIsDrawerOpen(false);
    };

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isDrawerOpen) {
                closeDrawer();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isDrawerOpen]);

    useEffect(() => {
        if (isDrawerOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isDrawerOpen]);

    return (
        <>
            {/* Desktop Navigation - Floating Pill */}
            <nav className={`hidden lg:block fixed left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ${
                isScrolled ? 'top-4 opacity-100 translate-y-0' : 'top-4 opacity-0 -translate-y-4 pointer-events-none'
            }`}>
                <div className={`bg-white border border-border-light rounded-full px-3 py-2 flex items-center gap-1 transition-all duration-300 ${
                    isScrolled ? 'shadow-medium' : ''
                }`}>
                    {/* Logo */}
                    <button
                        onClick={scrollToTop}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-full hover:bg-cream-200 transition-colors"
                    >
                        <div className="w-6 h-6 bg-coral-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs font-semibold">M</span>
                        </div>
                        <span className="font-medium text-charcoal-900 text-sm">Manav Gupta</span>
                    </button>

                    {/* Divider */}
                    <div className="w-px h-6 bg-border-light mx-2" />

                    {/* Nav Links */}
                    <div className="flex items-center">
                        {navItems.map(item => (
                            <button
                                key={item.id}
                                onClick={() => handleNavItem(item)}
                                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                                    isNavItemActive(item)
                                        ? 'text-charcoal-900 bg-cream-200'
                                        : 'text-charcoal-600 hover:text-charcoal-900 hover:bg-cream-100'
                                }`}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>

                    {/* Divider */}
                    <div className="w-px h-6 bg-border-light mx-2" />

                    {/* CTA Button */}
                    <a
                        href={personalInfo?.resume || '/resume.pdf'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-1.5 bg-coral-500 text-white rounded-full text-sm font-medium hover:bg-coral-600 transition-colors"
                    >
                        Resume
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                    </a>
                </div>
            </nav>

            {/* Tablet Navigation */}
            <nav className={`hidden md:block lg:hidden fixed left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ${
                isScrolled ? 'top-4 opacity-100 translate-y-0' : 'top-4 opacity-0 -translate-y-4 pointer-events-none'
            }`}>
                <div className={`bg-white border border-border-light rounded-full px-4 py-2.5 flex items-center gap-4 transition-all duration-300 ${
                    isScrolled ? 'shadow-medium' : ''
                }`}>
                    <button
                        onClick={scrollToTop}
                        className="flex items-center gap-2"
                    >
                        <div className="w-6 h-6 bg-coral-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs font-semibold">M</span>
                        </div>
                    </button>

                    <div className="flex items-center gap-1">
                        {navItems.map(item => (
                            <button
                                key={item.id}
                                onClick={() => handleNavItem(item)}
                                className={`px-3 py-1 rounded-full text-sm font-medium transition-all duration-200 ${
                                    isNavItemActive(item)
                                        ? 'text-charcoal-900 bg-cream-200'
                                        : 'text-charcoal-600 hover:text-charcoal-900'
                                }`}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>

                    <a
                        href={personalInfo?.resume || '/resume.pdf'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-coral-500 text-white rounded-full text-sm font-medium hover:bg-coral-600 transition-colors"
                    >
                        CV
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                    </a>
                </div>
            </nav>

            {/* Mobile Menu Button */}
            <button
                onClick={toggleDrawer}
                className={`md:hidden fixed top-4 right-4 z-50 bg-white border border-border-light rounded-full p-3 text-charcoal-700 hover:text-charcoal-900 transition-all duration-300 ${
                    isScrolled ? 'opacity-100 translate-y-0 shadow-medium' : 'opacity-0 -translate-y-4 pointer-events-none'
                }`}
                aria-label="Toggle navigation menu"
            >
                <MenuIcon className="w-5 h-5" />
            </button>

            {/* Mobile Logo */}
            <button
                onClick={scrollToTop}
                className={`md:hidden fixed top-4 left-4 z-50 bg-white border border-border-light rounded-full px-3 py-2.5 flex items-center gap-2 transition-all duration-300 ${
                    isScrolled ? 'opacity-100 translate-y-0 shadow-medium' : 'opacity-0 -translate-y-4 pointer-events-none'
                }`}
            >
                <div className="w-6 h-6 bg-coral-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-semibold">M</span>
                </div>
            </button>

            {/* Mobile Floating Go to Top Button */}
            <button
                onClick={scrollToTop}
                className={`md:hidden fixed bottom-6 right-6 z-40 w-11 h-11 bg-white border border-border-light rounded-full flex items-center justify-center text-charcoal-600 hover:bg-coral-50 hover:text-coral-600 transition-all duration-300 ${
                    isScrolled ? 'opacity-100 translate-y-0 shadow-medium' : 'opacity-0 translate-y-4 pointer-events-none'
                }`}
                aria-label="Go to top"
            >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
            </button>

            {/* Mobile Drawer Overlay */}
            {isDrawerOpen && (
                <div
                    className="md:hidden fixed inset-0 bg-charcoal-900/30 backdrop-blur-sm z-40 transition-opacity duration-300"
                    onClick={closeDrawer}
                    aria-hidden="true"
                />
            )}

            {/* Mobile Drawer */}
            <div className={`md:hidden fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white border-l border-border-light z-50 transform transition-transform duration-300 ease-out ${
                isDrawerOpen ? 'translate-x-0' : 'translate-x-full'
            }`}>
                {/* Drawer Header */}
                <div className="flex items-center justify-between p-6 border-b border-border-light">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-coral-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-semibold">M</span>
                        </div>
                        <span className="font-serif text-lg text-charcoal-900">Menu</span>
                    </div>
                    <button
                        onClick={closeDrawer}
                        className="text-charcoal-500 hover:text-charcoal-900 transition-colors p-1"
                        aria-label="Close navigation menu"
                    >
                        <CloseIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Drawer Navigation Items */}
                <nav className="p-6">
                    <ul className="space-y-2">
                        {navItems.map(item => (
                            <li key={item.id}>
                                <button
                                    onClick={() => handleNavItem(item)}
                                    className={`w-full text-left py-3 px-4 rounded-xl font-medium transition-all duration-200 ${
                                        isNavItemActive(item)
                                            ? 'bg-coral-50 text-coral-600'
                                            : 'text-charcoal-700 hover:bg-cream-100 hover:text-charcoal-900'
                                    }`}
                                >
                                    {item.label}
                                </button>
                            </li>
                        ))}
                    </ul>

                    {/* Mobile CTA */}
                    <div className="mt-8 pt-6 border-t border-border-light">
                        <a
                            href={personalInfo?.resume || '/resume.pdf'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 w-full py-3 bg-coral-500 text-white rounded-full font-medium hover:bg-coral-600 transition-colors"
                        >
                            Download Resume
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                        </a>
                    </div>
                </nav>
            </div>
        </>
    );
};

export default Navbar;
